// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Projects Page
// Route: /projects
// apps/web/src/routes/projects.tsx
//
// Full project portfolio view with CRUD, status filtering, compression
// metrics, and navigation to project detail pages.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  NeonValue,
  EngineTooltip,
  MetricCard,
  StatusBadge,
  LoadingShimmer,
  EmptyState,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { HelpBubble } from '../components/engine/HelpBubble';
import { useProjects, useProjectMutations } from '../lib/api/projects';
import type { Project, ProjectStatus, CreateProjectRequest } from '../lib/api/types';

export const Route = createFileRoute('/projects/')({
  component: ProjectsPage,
});

// ─── Status Config ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ProjectStatus, { color: string; icon: string }> = {
  Planning: { color: C.blue, icon: '📋' },
  Active: { color: C.neon, icon: '⚡' },
  OnHold: { color: C.amber, icon: '⏸' },
  Completed: { color: C.cyan, icon: '✓' },
  Archived: { color: C.dim, icon: '📦' },
};

// ─── Main Component ─────────────────────────────────────────────────────────

function ProjectsPage() {
  const { data: projects, loading, error, refetch } = useProjects();
  const mutations = useProjectMutations();
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');

  const filtered = useMemo(() => {
    if (!projects) return [];
    let list = showArchived ? projects : projects.filter(p => !p.isArchived);
    if (statusFilter !== 'all') {
      list = list.filter(p => p.status === statusFilter);
    }
    return list;
  }, [projects, showArchived, statusFilter]);

  const metrics = useMemo(() => {
    if (!projects) return null;
    const active = projects.filter(p => p.status === 'Active').length;
    const totalHours = projects.reduce((s, p) => s + p.totalActualHours, 0);
    const totalWeeks = projects.reduce((s, p) => s + p.totalEquivalentWeeks, 0);
    const totalCost = projects.reduce((s, p) => s + p.replacementCostEstimate, 0);
    return { total: projects.length, active, totalHours, totalWeeks, totalCost };
  }, [projects]);

  const countWeeks = useCounter(metrics?.totalWeeks ?? 0, 900, 300);
  const countCost = useCounter(metrics ? Math.round(metrics.totalCost ?? 0) : 0, 900, 400);

  // Smart $ formatter: switches K → M → B based on magnitude so
  // portfolio totals don't display as "$23,553K".
  const formatMoney = (dollars: number): string => {
    if (dollars >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(2)}B`;
    if (dollars >= 1_000_000)     return `$${(dollars / 1_000_000).toFixed(1)}M`;
    return `$${Math.round(dollars / 1000).toLocaleString()}K`;
  };

  if (error) {
    return (
      <EnginePageShell title="Projects" subtitle="R&D PORTFOLIO" icon="📐" accentColor={C.coral}>
        <GlassPanel accent={C.coral}>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠</div>
            <div style={{ fontSize: 16, color: C.coral, fontWeight: 600, marginBottom: 8 }}>
              Projects unavailable
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {error.message}
            </div>
            <button onClick={refetch} style={{
              background: `${C.coral}20`, border: `1px solid ${C.coral}40`, color: C.coral,
              fontFamily: F.mono, fontSize: 12, padding: '8px 24px', borderRadius: 6, cursor: 'pointer',
            }}>
              RETRY
            </button>
          </div>
        </GlassPanel>
      </EnginePageShell>
    );
  }

  return (
    <EnginePageShell
      title="Projects"
      subtitle="R&D PORTFOLIO"
      icon="📐"
      accentColor={C.cyan}
      statusLabel={`${metrics?.total ?? 0} TRACKED`}
      statusColor={C.cyan}
    >
      {/* ── Summary Metrics ───────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: 14,
        marginBottom: 24,
      }}>
        <HelpBubble helpKey="dashboard.active_projects">
          <MetricCard label="Projects" value={metrics?.total ?? 0} icon="📐" color={C.cyan}
            subtitle={`${metrics?.active ?? 0} active`}
            tooltip={<div>Total R&D projects in your portfolio.</div>} />
        </HelpBubble>
        <MetricCard label="Total Hours" value={`${metrics?.totalHours ?? 0}h`} icon="⏱" color={C.blue}
          tooltip={<div>Cumulative actual hours across all projects.</div>} />
        <HelpBubble helpKey="dashboard.equiv_weeks">
          <MetricCard label="Equiv. Output" value={`${countWeeks}w`} icon="🧬" color={C.neon}
            tooltip={<div>Total equivalent engineering-weeks delivered.</div>} />
        </HelpBubble>
        <HelpBubble helpKey="dashboard.replacement_cost">
          <MetricCard label="Portfolio Value" value={formatMoney(countCost)} icon="💰" color={C.violet}
            tooltip={<div>Total market replacement cost across all projects.</div>} />
        </HelpBubble>
      </div>

      {/* ── Filters + Actions ─────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(['all', 'Active', 'Planning', 'OnHold', 'Completed'] as const).map(status => {
            const isActive = statusFilter === status;
            const cfg = status === 'all' ? { color: C.cyan } : STATUS_CONFIG[status];
            return (
              <button key={status} onClick={() => setStatusFilter(status)} style={{
                background: isActive ? `${cfg.color}18` : 'transparent',
                border: `1px solid ${isActive ? `${cfg.color}50` : C.faint}`,
                color: isActive ? cfg.color : C.dim,
                fontFamily: F.mono, fontSize: 10, padding: '5px 14px', borderRadius: 6,
                cursor: 'pointer', fontWeight: 600, letterSpacing: '0.06em',
              }}>
                {status === 'all' ? 'ALL' : status.toUpperCase()}
              </button>
            );
          })}
          <button onClick={() => setShowArchived(!showArchived)} style={{
            background: showArchived ? `${C.dim}20` : 'transparent',
            border: `1px solid ${showArchived ? `${C.dim}50` : C.faint}`,
            color: showArchived ? C.textSoft : C.dim,
            fontFamily: F.mono, fontSize: 10, padding: '5px 14px', borderRadius: 6,
            cursor: 'pointer', letterSpacing: '0.06em',
          }}>
            {showArchived ? '📦 ARCHIVED' : 'SHOW ARCHIVED'}
          </button>
        </div>
        <button onClick={() => setShowCreate(true)} style={{
          background: `${C.neon}12`, border: `1px solid ${C.neon}30`, color: C.neon,
          fontFamily: F.mono, fontSize: 10, padding: '6px 16px', borderRadius: 6,
          cursor: 'pointer', fontWeight: 600,
        }}>
          + NEW PROJECT
        </button>
      </div>

      {/* ── Project List ──────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2].map(i => (
            <GlassPanel key={i} delay={0.2 + i * 0.08}>
              <LoadingShimmer height={100} />
            </GlassPanel>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <GlassPanel accent={C.cyan} delay={0.2}>
          <EmptyState
            icon="📐"
            title={statusFilter !== 'all' ? `No ${statusFilter.toLowerCase()} projects` : 'No projects yet'}
            subtitle="Create your first project to start tracking R&D output, compression ratios, and replacement cost."
            action={
              <button onClick={() => setShowCreate(true)} style={{
                background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
                fontFamily: F.mono, fontSize: 12, padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
              }}>+ CREATE PROJECT</button>
            }
          />
        </GlassPanel>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>
      )}

      {/* ── Create Modal ──────────────────────────────────────────── */}
      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreate={async (data) => {
            const result = await mutations.create(data);
            if (result) { setShowCreate(false); refetch(); }
          }}
          loading={mutations.loading}
        />
      )}
    </EnginePageShell>
  );
}

// ─── ProjectCard ─────────────────────────────────────────────────────────────

function ProjectCard({ project: p, index }: { project: Project; index: number }) {
  const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.Active;
  const maxWeeks = Math.max(p.totalEquivalentWeeks, 1);
  const countRatio = useCounter(p.compressionRatio, 800, 300 + index * 80);
  const countCost = useCounter(
    p.replacementCostEstimate >= 1_000_000
      ? p.replacementCostEstimate / 1_000_000
      : p.replacementCostEstimate / 1_000,
    800, 400 + index * 80
  );
  const costUnit = p.replacementCostEstimate >= 1_000_000 ? 'M' : 'K';

  return (
    <GlassPanel accent={cfg.color} delay={0.15 + index * 0.06}>
      <Link
        to="/projects/$projectId"
        params={{ projectId: String(p.id) }}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: 'pointer' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{p.name}</span>
              <StatusBadge label={p.status} color={cfg.color} />
            </div>
            {p.description && (
              <div style={{ fontSize: 12, color: C.muted, maxWidth: 600, lineHeight: 1.5 }}>
                {p.description}
              </div>
            )}
          </div>
          <EngineTooltip content={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6, color: C.text }}>Compression Analysis</div>
              <div style={{ lineHeight: 1.6 }}>
                <span style={{ color: C.blue }}>{p.totalActualHours}h</span> actual →{' '}
                <span style={{ color: C.cyan }}>{p.totalEquivalentWeeks}w</span> equivalent →{' '}
                <span style={{ color: C.violet }}>${(p.replacementCostEstimate / 1000).toFixed(0)}K</span> value
              </div>
            </div>
          }>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <NeonValue color={C.neon} size={28} glow>
                {countRatio.toFixed(p.compressionRatio >= 10 ? 0 : 1)}×
              </NeonValue>
              <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginTop: 2 }}>
                ${countCost.toFixed(costUnit === 'M' ? 1 : 0)}{costUnit} value
              </div>
            </div>
          </EngineTooltip>
        </div>

        {/* Effort bar */}
        <div style={{ marginBottom: 8 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 4,
          }}>
            <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
              {p.totalEquivalentWeeks}w equivalent · {p.totalActualHours}h actual
            </span>
          </div>
          <div style={{
            height: 4, background: `${C.faint}`, borderRadius: 2, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${Math.min((p.totalEquivalentWeeks / Math.max(maxWeeks, 200)) * 100, 100)}%`,
              background: `linear-gradient(90deg, ${cfg.color}60, ${cfg.color})`,
              borderRadius: 2,
              animation: `engine-bar-fill 1s ease ${0.3 + index * 0.06}s both`,
            }} />
          </div>
        </div>

        {/* Tags + date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(p.tags ?? []).map(tag => (
              <span key={tag} style={{
                fontSize: 9, fontFamily: F.mono, letterSpacing: '0.06em',
                color: C.dim, background: C.faint, padding: '2px 6px',
                borderRadius: 4, textTransform: 'uppercase',
              }}>
                {tag}
              </span>
            ))}
            {p.phaseCount != null && (
              <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>
                {p.phaseCount} phase{p.phaseCount !== 1 ? 's' : ''}
              </span>
            )}
            {p.workItemCount != null && (
              <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>
                · {p.workItemCount} items
              </span>
            )}
          </div>
          <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>
            {new Date(p.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
        </div>
      </Link>
    </GlassPanel>
  );
}

// ─── CreateProjectModal ──────────────────────────────────────────────────────

function CreateProjectModal({ onClose, onCreate, loading }: {
  onClose: () => void;
  onCreate: (data: CreateProjectRequest) => void;
  loading: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [tags, setTags] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
    fontSize: 13, outline: 'none',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'engine-tooltip-in 0.2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.panel, border: `1px solid ${C.cyan}20`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 520,
      }}>
        <SectionLabel color={C.cyan}>New Project</SectionLabel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
              PROJECT NAME
            </div>
            <input value={name} onChange={e => setName(e.target.value)} style={inputStyle}
              placeholder="e.g., LoanForge, ForgeTrack" autoFocus />
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
              DESCRIPTION
            </div>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              placeholder="AI-powered commercial lending platform..." />
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
              REPOSITORY URL
            </div>
            <input value={repoUrl} onChange={e => setRepoUrl(e.target.value)} style={inputStyle}
              placeholder="https://github.com/org/repo" />
          </div>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
              TAGS (comma-separated)
            </div>
            <input value={tags} onChange={e => setTags(e.target.value)} style={inputStyle}
              placeholder="fintech, AI, lending" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted,
            fontFamily: F.mono, fontSize: 11, padding: '8px 20px', borderRadius: 6, cursor: 'pointer',
          }}>CANCEL</button>
          <button
            disabled={!name.trim() || loading}
            onClick={() => onCreate({
              name: name.trim(),
              description: description.trim() || undefined,
              repositoryUrl: repoUrl.trim() || undefined,
              tags: tags.split(',').map(t => t.trim()).filter(Boolean),
              status: 'Active',
            })}
            style={{
              background: name.trim() ? `${C.neon}20` : `${C.dim}10`,
              border: `1px solid ${name.trim() ? `${C.neon}40` : C.faint}`,
              color: name.trim() ? C.neon : C.dim,
              fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '8px 24px',
              borderRadius: 6, cursor: name.trim() ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'CREATING...' : 'CREATE PROJECT'}</button>
        </div>
      </div>
    </div>
  );
}
